import {ITableColumn, TBaseOrObservableOrFunctionArrayType} from "../../index";
import {_} from 'lodash';
import {isFunction} from "lodash";
import {storeObservable} from "../../util/sessionstore";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {ISortableTableParams} from "./index";
import {getAnyValue, getKnockoutThrottle, isSupportedIcon} from "../../util/misc";
import {nsSearchToggle} from "../search/nsSearchToggle";
import {IAllCheckbox} from "../search/th-checkbox";
import {IItemCheckbox} from "../search/td-checkbox";
import {hasher} from 'hasher';
import * as ko from "knockout";

export let template: string = sortable_tableHtml;
import ItemCheckbox = nsSearchToggle.ItemCheckbox;
import sortable_tableHtml from './sortable-table.html?raw';

export class viewModel<T, K = keyof T, V = T[keyof T]> {

    private readonly TABLE: TBaseOrObservableOrFunctionArrayType<ITableColumn<T>>
    private readonly asc: KnockoutObservable<boolean>;
    private readonly data: KnockoutObservableArray<T> | KnockoutComputed<T[]>
    private readonly dataSorted: KnockoutComputed<T[]>
    private readonly kosub = new KOSubscriptionTracker();
    private readonly route: string | undefined;
    private readonly searchkey: KnockoutObservable<string> | undefined;
    private readonly sortcol: KnockoutObservable<keyof T>;

    private readonly pagenr: KnockoutObservable<number>
    private readonly pages: KnockoutComputed<number> = ko.pureComputed(() => Math.ceil(getAnyValue(this.data).length / this.resultsprpage()));
    private readonly resultsprpage = storeObservable('num.search.results');
    private readonly totalresults: KnockoutComputed<number> = ko.pureComputed(() => getAnyValue(this.data).length);
    private readonly allCheckbox: IAllCheckbox<T> | undefined

    constructor(protected params: ISortableTableParams<T>) {
        this.params = _.extend({
            isSelected: this.isSelected,
            isDisabled: this.isDisabled,
        }, this.params);

        this.TABLE = params.TABLE;
        if (_.isArray(params.data)) {
            this.data = ko.observableArray<T>(params.data as T[]);
        } else {
            this.data = params.data as KnockoutComputed<T[]>;
        }
        this.route = params.route;
        this.searchkey = params.searchkey
        this.pagenr = params.pagenr ?? ko.observable(0)

        if (this.searchkey) {
            this.searchkey.extend(getKnockoutThrottle())
        }

        if ('dataPage' in params) {
            this.dataSorted = ko.pureComputed(() => getAnyValue(params.dataPage) as T[])
        } else {
            this.dataSorted = ko.pureComputed((): T[] => {
                const d = ko.unwrap(this.data);
                const sortCol = this.sortcol();
                const col = this.getTableColumn(sortCol)
                let ret = _.sortBy(d, (item: T) => {
                    if (col && 'getSortValue' in col && col.getSortValue) {
                        return col.getSortValue(item)
                    } else if ('getCellSortValue' in this.params && this.params.getCellSortValue) {
                        return this.params.getCellSortValue(sortCol, item[sortCol as keyof T], item);
                    }
                    const i = this.getCellText(sortCol, item[sortCol as keyof T], item);
                    if (_.isString(i)) return i.toLowerCase();
                    if (_.isArray(i)) return (i as unknown as unknown[]).length;
                    return i;
                });
                ret = this.asc() ? ret : ret.reverse();
                if (this.params.showAll) {
                    return ret;
                }
                const start = this.pagenr() * this.resultsprpage();
                const end = start + this.resultsprpage();
                return ret.slice(start, end);
            })
        }

        if (params.onRowClick) {
            this.onRowClick = params.onRowClick as (row: T) => boolean;
        }

        if (params.onRowKeyUp) {
            this.onRowKeyUp = params.onRowKeyUp;
        }

        const tableColumns = getAnyValue(this.TABLE);
        if (params.sortcol) {
            this.sortcol = params.sortcol;
            const sortColumnExists = tableColumns.find(o => o.key == this.sortcol());
            if (!sortColumnExists && tableColumns[0]) {
                this.sortcol(tableColumns[0].key as keyof T);
            }
        } else {
            this.sortcol = ko.observable((tableColumns[0]?.key ?? 'id') as keyof T);
        }

        if (params.asc) {
            this.asc = params.asc;
        } else {
            this.asc = ko.observable(true);
        }

        if (params.allCheckbox) {
            // Keep the checkbox instance (class methods are non-enumerable; _.defaults would drop them).
            this.allCheckbox = Object.assign(params.allCheckbox, {data: this.dataSorted});
        }

        this.kosub.subscribe([this.data, this.resultsprpage, this.sortcol, this.asc], () => this.pagenr(0));
    }

    getCellContent(key: keyof T, val: T[keyof T], item: T): string | number | any[] {
        let ret: any = val;
        const col = this.getTableColumn(key);
        if (col?.yesno) {
            ret = isSupportedIcon(val as boolean)
        } else if (this.params.getCellContent) {
            ret = this.params.getCellContent(key, val, item);
        } else if (this.params.getCellText) {
            ret = this.params.getCellText(key, val, item);
        }

        return ret;
    }

    protected getTableColumn(key: keyof T | string): ITableColumn<T> {
        return getAnyValue(this.TABLE).find((o: ITableColumn<T>) => o.key == key)!;
    }

    getCellText(key: keyof T, val: T[keyof T], item: T): string {
        if (this.params.getCellText) {
            return this.params.getCellText(key, val, item);
        } else {
            return String(val);
        }
    }

    getCellTitle(key: keyof T, val: T[keyof T], item: T): string | null {
        const getCellTitle = this.params.getCellTitle;
        if (getCellTitle) {
            return getCellTitle(key, val, item) ?? null;
        } else {
            return this.getCellText(key, val, item) ?? null;
        }
    }

    isSelected() {
        return false;
    }

    isDisabled() {
        return false;
    }

    getCellCss(obj: T, col: ITableColumn<T>) {
        let css = ['cell', 'cell__' + String(col.key)];
        if (col.sort) {
            css.push('cell--sortable');
        }
        if (col.center) {
            css.push('text-center');
        }
        if ('getCellCss' in col && col.getCellCss) {
            css.push(col.getCellCss(obj))
        }
        if (col.css) {
            css.push(col.css)
        }
        return css.join(" ");
    }

    isSearchableColumn() {
        return !!this.searchkey;
    }

    onRowClick = (row: T): boolean => {
        if ((this.params.isDisabled?.(row) ?? false) || !this.route) {
            return true;
        }
        hasher.setHash(`${this.route}/` + (row as any).id);
        return true;
    };

    onRowKeyUp = (row: T, event: KeyboardEvent) => {
        if (this.params.isDisabled?.(row) ?? false) {
            return;
        }
        if (event.keyCode === 13) {
            this.onRowClick(row);
        }
    };

    private getItemCheckbox(item: T): IItemCheckbox {
        if ('getItemCheckbox' in this.params && this.params.getItemCheckbox) {
            return this.params.getItemCheckbox(item)
        }
        return new ItemCheckbox({
            item: item,
            key: this.allCheckbox!.key,
            selected: this.allCheckbox!.selected,
        })
    }

    protected getContentType(col: ITableColumn<T>, obj: T) {
        if (col.buttons) {
            return 'buttons'
        }
        if (col.key == 'selector') {
            return 'selector'
        }
        if (col.key == 'func') {
            return 'func'
        }
        if (col.component) {
            if (isFunction(col.component) && col.component(obj)) {
                return 'component'
            }
        }
        if (col.getComponent) {
            if (isFunction(col.getComponent) && col.getComponent(obj)) {
                return 'getComponent'
            }
        }
        if (col.html !== false) {
            if (col.binding === true) {
                return 'htmlWithBinding'
            } else {
                return 'html'
            }
        }
        return 'default';
    }


    dispose() {
        this.kosub.unsuscribeAll();
    }

}
